import React, { useRef, useState } from 'react';
import { AuctionWinsCard } from '../components/AuctionWinsCard';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Phone, Award, LogOut, Briefcase, ShieldCheck, Camera, Loader2, Heart } from 'lucide-react';
import { files as filesApi } from '../services/api';
import { toast } from '../hooks/use-toast';
import NotificationPrefsCard from '../components/NotificationPrefsCard';

export default function Profile() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const pick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) { toast({ title: 'Please choose an image file' }); return; }
    setUploading(true);
    try {
      await filesApi.upload(f, 'avatar');
      await refresh();
      toast({ title: 'Profile photo updated' });
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Try again' });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div className="pbb-card p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5">
        <div className="relative">
          <img src={filesApi.urlFor(user?.avatar)} alt="me" className="w-24 h-24 rounded-full border-2 border-green-500 object-cover"/>
          <button onClick={pick} disabled={uploading}
            className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-green-600 hover:bg-green-700 border-2 border-black flex items-center justify-center text-white disabled:opacity-70">
            {uploading ? <Loader2 size={14} className="animate-spin"/> : <Camera size={14}/>}
          </button>
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFile}/>
        </div>
        <div className="flex-1">
          <div className="text-2xl font-bold">{user?.name}</div>
          <div className="text-xs text-green-400 mt-1">{user?.tier} Member • Since {user?.memberSince}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3 text-sm text-gray-300">
            <div className="inline-flex items-center gap-2"><Mail size={14}/> {user?.email}</div>
            <div className="inline-flex items-center gap-2"><Phone size={14}/> {user?.phone}</div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="pbb-card p-5">
          <Award className="text-green-400" size={22}/>
          <div className="text-xs text-gray-400 mt-2">Points Balance</div>
          <div className="text-2xl font-bold text-green-400">{user?.points?.toLocaleString()}</div>
        </div>
        <div className="pbb-card p-5">
          <ShieldCheck className="text-green-400" size={22}/>
          <div className="text-xs text-gray-400 mt-2">Auction Subscription</div>
          <div className="text-lg font-semibold">{user?.subscribed ? 'Active' : 'Inactive'}</div>
        </div>
      </div>
      <div className="pbb-card p-5">
        <div className="flex items-center gap-3">
          <Briefcase className="text-green-400" size={20}/>
          <div className="flex-1">
            <div className="font-medium">Business Owner Mode</div>
            <div className="text-xs text-gray-400">Unlock the business dashboard, coupon manager, and analytics.</div>
          </div>
          <div className={`px-3 py-1.5 rounded-lg text-sm ${user?.isBusinessOwner ? 'bg-green-600 text-white' : 'border border-[#2a2a2a] text-gray-300'}`}>
            {user?.isBusinessOwner ? 'Enabled' : 'Not enabled'}
          </div>
        </div>
      </div>
      <Link to="/profile/interests" data-testid="profile-my-interests-link"
        className="pbb-card p-5 flex items-center gap-3 hover:border-pink-500 transition-colors">
        <Heart className="text-pink-400" size={22}/>
        <div className="flex-1">
          <div className="font-medium text-white">My Interests</div>
          <div className="text-xs text-gray-400">Preferences, completed surveys, and personalization settings.</div>
        </div>
        <div className="text-gray-500 text-sm">›</div>
      </Link>
      <AuctionWinsCard/>
      <NotificationPrefsCard/>
      <button onClick={() => { logout(); nav('/login'); }} className="w-full pbb-card p-4 flex items-center justify-center gap-2 text-red-400 hover:border-red-500">
        <LogOut size={16}/> Sign out
      </button>
    </div>
  );
}
